export type SendAs = "body" | "header" | "query";
export type ValueSource = "fixed" | "conversation" | "callVariable";
export type TriggerEvent = "callStart" | "duringCall" | "callEnd" | "callStatusWebhook";

export type InputParam = {
  _id?: any;
  paramName: string;
  persistParam: boolean;
  paramType: "static" | "dynamic";
  paramValue?: string;
  paramLocation: SendAs;
  paramPrefix?: string;
  [key: string]: any;
};

export type RequestField = {
  name: string;
  sendAs: SendAs;
  valueSource: ValueSource;
  fixedValue: string;
  variableName: string;
  raw?: InputParam;
};

export const SEND_AS_OPTIONS: Array<{ value: SendAs; label: string }> = [
  { value: "body", label: "Body field" },
  { value: "header", label: "Header" },
  { value: "query", label: "URL parameter" },
];

export const VALUE_SOURCE_OPTIONS: Array<{ value: ValueSource; label: string; description: string }> = [
  { value: "fixed", label: "A fixed value", description: "The same every time" },
  { value: "conversation", label: "The conversation", description: "Pulled out by the detailed check" },
  { value: "callVariable", label: "A call variable", description: "Known for this call" },
];

export function newRequestField(): RequestField {
  return { name: "", sendAs: "body", valueSource: "fixed", fixedValue: "", variableName: "" };
}

export function fromInputParam(param: InputParam): RequestField {
  const name = param.paramName ?? "";
  const sendAs: SendAs = param.paramLocation ?? "body";
  if (param.persistParam) {
    return {
      name,
      sendAs,
      valueSource: "callVariable",
      fixedValue: "",
      variableName: (param.paramValue ?? "").trim() || name,
      raw: param,
    };
  }
  if (param.paramType === "dynamic") {
    return { name, sendAs, valueSource: "conversation", fixedValue: "", variableName: "", raw: param };
  }
  return { name, sendAs, valueSource: "fixed", fixedValue: param.paramValue ?? "", variableName: "", raw: param };
}

export function toInputParam(field: RequestField): InputParam {
  const name = (field.name ?? "").trim();
  const base: Partial<InputParam> = field.raw ? { ...field.raw } : {};

  let paramValue = "";
  if (field.valueSource === "callVariable") {
    const variable = (field.variableName ?? "").trim();
    paramValue = variable && variable !== name ? variable : "";
  } else if (field.valueSource === "fixed") {
    paramValue = field.fixedValue ?? "";
  }

  return {
    ...base,
    paramName: name,
    paramLocation: field.sendAs,
    persistParam: field.valueSource === "callVariable",
    paramType: field.valueSource === "conversation" ? "dynamic" : "static",
    paramValue,
  } as InputParam;
}

/** "The conversation" is only available where a trigger prompt is evaluated. */
export function valueSourcesFor(triggerEvent: TriggerEvent | string): ValueSource[] {
  return triggerEvent === "duringCall" || triggerEvent === "callEnd"
    ? ["fixed", "conversation", "callVariable"]
    : ["fixed", "callVariable"];
}


export function describeRequestValue(field: RequestField, options?: { conversationLabel?: string }): string {
  switch (field.valueSource) {
    case "conversation":
      return options?.conversationLabel ?? "Pulled out by the detailed check";
    case "callVariable":
      return `{${(field.variableName ?? "").trim() || field.name}}`;
    default:
      return field.sendAs === "header" && field.fixedValue ? "••••••••" : field.fixedValue ?? "";
  }
}
